import React from "react";
import { motion } from "framer-motion";
import shortLogo from "../../assets/Images/logo2.png";
import logo1 from '../../assets/Images/logo1.png'
import { COMPANY_NAME_MAIN, COMPANY_NAME_SUBTITLE, BRAND_NAME, COMPANY_TAGLINE } from '../../config/Constants';

interface CompanyLogoProps {
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl";
  variant?: "default" | "minimal" | "full" | "icon";
  className?: string;
  animated?: boolean;
  showTagline?: boolean;
  centered?: boolean;
  fullWidth?: boolean;
  customSize?: {
    width?: string;
    height?: string;
    textSize?: string;
    taglineSize?: string;
  };
}

  export const MainLogo = () => (
    <motion.div
      className="flex items-center gap-2"
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
    >
      {/* Infinity Symbol with gradient */}
      <img
        src={logo1}
        alt={`${BRAND_NAME} Logo`}
        className="mb-0 mx-auto w-1/2 max-w-xl h-auto"
        // className="mb-0 mx-auto h-auto w-40 sm:w-48 md:w-56 lg:w-64 xl:w-72"
      />
    </motion.div>
  )

const CompanyLogo: React.FC<CompanyLogoProps> = ({
  size = "md",
  variant = "default",
  className = "",
  animated = true,
  showTagline = true,
  centered = false,
  fullWidth = false,
  customSize,
}) => {
  const sizeClasses = {
    sm: "text-lg",
    md: "text-xl",
    lg: "text-2xl",
    xl: "text-3xl",
    "2xl": "text-4xl",
    "3xl": "text-5xl",
    "4xl": "text-6xl",
    "5xl": "text-7xl",
  };

  const iconSizes = {
    sm: "w-6 h-6",
    md: "w-8 h-8",
    lg: "w-10 h-10",
    xl: "w-12 h-12",
    "2xl": "w-16 h-16",
    "3xl": "w-20 h-20",
    "4xl": "w-24 h-24",
    "5xl": "w-32 h-32",
  };

  const taglineSizes = {
    sm: "text-xs",
    md: "text-sm",
    lg: "text-base",
    xl: "text-lg",
    "2xl": "text-xl",
    "3xl": "text-2xl",
    "4xl": "text-3xl",
    "5xl": "text-4xl",
  };

  // Use custom sizes if provided, otherwise fall back to predefined sizes
  const finalIconSize = customSize?.width && customSize?.height 
    ? `${customSize.width} ${customSize.height}`
    : iconSizes[size];
  
  const finalTextSize = customSize?.textSize || sizeClasses[size];
  const finalTaglineSize = customSize?.taglineSize || taglineSizes[size];

  const LogoIcon = () => (
    <motion.div
      className={`${finalIconSize} relative`}
      whileHover={animated ? { scale: 1.05, rotate: 5 } : {}}
      transition={{ duration: 0.2 }}
    >
      {/* Infinity Symbol with gradient */}
      <img
        src={shortLogo}
        alt={`${BRAND_NAME} Logo`}
        className={`${finalTextSize} object-contain`}
      />
    </motion.div>
  );

  

  const LogoText = () => (
    <motion.div
      className="flex flex-col "
      initial={animated ? { opacity: 0, x: -20 } : {}}
      animate={animated ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <div className="flex items-center gap-2 text-white">
        <span
          className={`font-bold ${finalTextSize} text-blue-600 dark:text-blue-400`}
        >
          {COMPANY_NAME_MAIN}
        </span>
        <span
          className={`font-bold ${finalTextSize} text-blue-600 dark:text-blue-600`}
        >
          {COMPANY_NAME_SUBTITLE}
        </span>
      </div>

      {showTagline && (
        <motion.div
          className={`${finalTaglineSize} text-blue-600 dark:text-gray-400 font-medium tracking-wide`}
          initial={animated ? { opacity: 0, y: -10 } : {}}
          animate={animated ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          {COMPANY_TAGLINE}
        </motion.div>
      )}
    </motion.div>
  );

  const MinimalLogo = () => (
    <motion.div
      className="flex items-center gap-2"
      whileHover={animated ? { scale: 1.02 } : {}}
      transition={{ duration: 0.2 }}
    >
      <LogoIcon />
    </motion.div>
  );

  const FullLogo = () => (
    <motion.div
      className="flex items-center gap-3"
      whileHover={animated ? { scale: 1.02 } : {}}
      transition={{ duration: 0.2 }}
    >
      <LogoIcon />
      <LogoText />
    </motion.div>
  );

  const DefaultLogo = () => (
    <motion.div
      className="flex items-center gap-2"
      whileHover={animated ? { scale: 1.02 } : {}}
      transition={{ duration: 0.2 }}
    >
      <LogoIcon />
      <div className="flex flex-col">
        <span
          className={`font-bold ${finalTextSize} bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent`}
        >
          {COMPANY_NAME_MAIN}  {COMPANY_NAME_SUBTITLE}
        </span>
        {showTagline && (
          <span
            className={`${finalTaglineSize} text-gray-500 dark:text-gray-400`}
          >
            Production Workflow
          </span>
        )}
      </div>
    </motion.div>
  );
  
  const renderLogo = () => {
    switch (variant) {
      case "icon":
        return <LogoIcon />;
      case "minimal":
        return <MinimalLogo />;
      case "full":
        return <FullLogo />;
      default:
        return <DefaultLogo />;
    }
  };

  // Build container classes
  const containerClasses = [
    "inline-flex items-center",
    centered && "justify-center",
    fullWidth && "w-full",
    className
  ].filter(Boolean).join(" ");

  return (
    <div className={containerClasses}>
      {renderLogo()}
    </div>
  );
};

export default CompanyLogo;
